import React, { Component } from 'react';
import { connect } from 'react-redux';
import ScrollArea from 'react-scrollbar';
import Dialog from 'material-ui/Dialog';

import TextField from '../../components/basic/TextField';
import Button from '../../components/basic/Button';
import { Section, SectionItem } from '../../components/basic/section';

const STATUS_STYLE = {
  SENT: { color: '#2e7d32', backgroundColor: '#e8f5e9', label: 'SENT' },
  SKIPPED: { color: '#1565c0', backgroundColor: '#e3f2fd', label: 'SKIPPED' },
  ERROR: { color: '#c62828', backgroundColor: '#ffebee', label: 'ERROR' }
};

const parsePhoneNumbers = input =>
  (input || '')
    .split(/[\s,;]+/)
    .map(s => s.trim())
    .filter(s => s.length > 0);

class SmsTestContainer extends Component {
  state = {
    text: '',
    phoneNumbersInput: '',
    loading: false,
    confirmOpen: false,
    error: null,
    results: null
  };

  handleChange = e => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  openConfirm = e => {
    if (e && e.preventDefault) e.preventDefault();
    const { text, phoneNumbersInput } = this.state;
    const phoneNumbers = parsePhoneNumbers(phoneNumbersInput);
    if (!text.trim()) {
      this.setState({ error: 'Text is required' });
      return;
    }
    if (phoneNumbers.length === 0) {
      this.setState({ error: 'At least one phone number is required' });
      return;
    }
    this.setState({ error: null, confirmOpen: true });
  };

  closeConfirm = () => this.setState({ confirmOpen: false });

  doSend = async () => {
    const { auth } = this.props;
    const { text, phoneNumbersInput } = this.state;
    const phoneNumbers = parsePhoneNumbers(phoneNumbersInput);
    this.setState({ loading: true, confirmOpen: false, error: null, results: null });
    try {
      const response = await fetch('/api/v1/admin/sms/test', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.access_token}`
        },
        body: JSON.stringify({ text: text.trim(), phoneNumbers })
      });
      if (!response.ok) {
        let msg = `Request failed with status ${response.status}`;
        try {
          const body = await response.json();
          if (body && (body.errorMessage || body.error || body.message)) {
            msg = body.errorMessage || body.error || body.message;
          }
        } catch (_) {
          /* ignore */
        }
        throw new Error(msg);
      }
      const json = await response.json();
      const payload = (json && (json.payload || json.data)) || {};
      const results = payload.results || [];
      this.setState({ results });
    } catch (error) {
      console.error('Error sending test SMS:', error);
      this.setState({ error: error.message || 'Failed to send test SMS' });
    } finally {
      this.setState({ loading: false });
    }
  };

  renderStatusBadge = status => {
    const s = STATUS_STYLE[status] || { color: '#555', backgroundColor: '#eee', label: status };
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '10px',
          fontSize: '11px',
          fontWeight: 600,
          color: s.color,
          backgroundColor: s.backgroundColor
        }}
      >
        {s.label}
      </span>
    );
  };

  renderResults() {
    const { results } = this.state;
    if (!results) return null;
    return (
      <Section title="Results">
        <SectionItem>
          <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'rgb(158, 158, 158)' }}>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontWeight: 'normal' }}>
                  Phone number
                </th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontWeight: 'normal' }}>Status</th>
                <th style={{ padding: '6px 8px', borderBottom: '1px solid #eee', fontWeight: 'normal' }}>Message</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, idx) => (
                <tr key={`${r.phoneNumber}-${idx}`}>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5', fontFamily: 'monospace' }}>
                    {r.phoneNumber}
                  </td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>
                    {this.renderStatusBadge(r.status)}
                  </td>
                  <td style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5', color: '#555' }}>
                    {r.message || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionItem>
      </Section>
    );
  }

  render() {
    const { text, phoneNumbersInput, loading, confirmOpen, error } = this.state;
    const parsedNumbers = parsePhoneNumbers(phoneNumbersInput);

    const confirmActions = [
      <Button key="cancel" label="Cancel" onClick={this.closeConfirm} />,
      <Button key="send" primary raised label="Send SMS" onClick={this.doSend} />
    ];

    return (
      <form className="Form" onSubmit={this.openConfirm}>
        <ScrollArea>
          <div className="Form-content-container">
            <div className="container">
              <div className="row">
                <div className="col-md-8">
                  <Section title="Send test SMS">
                    <SectionItem>
                      <div style={{ fontSize: '12px', color: '#b26a00', marginBottom: '8px' }}>
                        Warning: this sends a real SMS via the configured provider (Twilio) and is billed.
                      </div>
                    </SectionItem>
                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <TextField
                        id="text"
                        name="text"
                        value={text}
                        onChange={this.handleChange}
                        label="SMS text"
                        hintText="Enter the SMS body"
                        fullWidth
                        multiLine
                        rows={4}
                        rowsMax={10}
                      />
                    </SectionItem>
                    <SectionItem contentStyle={{ paddingTop: 0 }}>
                      <TextField
                        id="phoneNumbersInput"
                        name="phoneNumbersInput"
                        value={phoneNumbersInput}
                        onChange={this.handleChange}
                        label="Phone numbers"
                        hintText="One per line or comma-separated, E.164 format (e.g. +420777111222)"
                        fullWidth
                        multiLine
                        rows={4}
                        rowsMax={10}
                        inputStyle={{ fontFamily: 'monospace', fontSize: '12px' }}
                      />
                      <div style={{ fontSize: '11px', color: 'rgb(158, 158, 158)', marginTop: '4px' }}>
                        {parsedNumbers.length} recipient{parsedNumbers.length === 1 ? '' : 's'} parsed
                      </div>
                    </SectionItem>
                    {error && (
                      <SectionItem contentStyle={{ paddingTop: 0 }}>
                        <div className="Text-error Text-regular">{error}</div>
                      </SectionItem>
                    )}
                  </Section>

                  {this.renderResults()}
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>
        <div className="Form-buttons-container" style={{ overflow: 'hidden' }}>
          <div
            className="Form-buttons flex flex1 flex-align-center"
            style={{ gap: '1rem', justifyContent: 'flex-end' }}
          >
            <div>
              <Button label="Send" primary raised loading={loading} type="submit" />
            </div>
          </div>
        </div>

        <Dialog
          open={confirmOpen}
          actions={confirmActions}
          modal={false}
          onRequestClose={this.closeConfirm}
          title="Confirm sending test SMS"
          contentStyle={{ minWidth: '320px', maxWidth: '640px' }}
        >
          <div style={{ fontSize: '13px' }}>
            <p style={{ marginTop: 0 }}>
              You are about to send a <b>real SMS</b> to{' '}
              <b>
                {parsedNumbers.length} recipient{parsedNumbers.length === 1 ? '' : 's'}
              </b>
              . This will be billed by the provider.
            </p>
            <div style={{ marginBottom: '8px', color: 'rgb(158, 158, 158)', fontSize: '11px' }}>Text</div>
            <div
              style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '12px',
                backgroundColor: 'rgba(70, 97, 124, 0.05)',
                borderRadius: '4px',
                padding: '8px',
                marginBottom: '12px'
              }}
            >
              {text}
            </div>
            <div style={{ marginBottom: '8px', color: 'rgb(158, 158, 158)', fontSize: '11px' }}>Recipients</div>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '12px',
                backgroundColor: 'rgba(70, 97, 124, 0.05)',
                borderRadius: '4px',
                padding: '8px',
                maxHeight: '200px',
                overflowY: 'auto'
              }}
            >
              {parsedNumbers.map(n => (
                <div key={n}>{n}</div>
              ))}
            </div>
          </div>
        </Dialog>
      </form>
    );
  }
}

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(SmsTestContainer);



// WEBPACK FOOTER //
// ./src/containers/private/SmsTestContainer.js